/**
 * LanguageSwitch - IT / EN in the navbar
 * ---------------------------------------
 * Shows the language you are reading in. Tap it and the other one appears
 * underneath; tap that and it takes over, and the site changes language.
 *
 * TWO LETTERS RATHER THAN FLAGS, and it was a deliberate change of mind.
 * A flag is a country, not a language: Italian is spoken in Switzerland and
 * San Marino too, and English has no flag that is right for a European school
 * - the UK is out of the EU and the American one would be stranger still.
 * Two letters are also much smaller on a phone, which is what nearly everybody
 * reads this on, and they never need explaining.
 *
 * IT SHOWS FOR SIGNED-OUT VISITORS TOO. The front page and the two signup
 * paths are the screens an Italian family sees first, and they are exactly the
 * screens somebody would give up on if the site arrived in a language they do
 * not read. A switch that only appears once you have an account is a switch
 * that arrives too late to matter.
 */

import { useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';

import { useI18n, LANGUAGES } from '../hooks/useI18n';
import { useAuth } from '../hooks/useAuth';
import { authApi } from '../services/api';

export default function LanguageSwitch() {
  const { lang, setLang, t } = useI18n();
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  /**
   * CHANGING THE LANGUAGE HERE ALSO CHANGES THE LANGUAGE OF YOUR EMAIL.
   *
   * The site works out its own language in the browser and remembers it in
   * localStorage. Email cannot: it is written by the server, hours or days
   * later, with no browser to ask - so it reads `language` on the account,
   * which was set once at signup.
   *
   * Those two drifted apart the moment anybody used this switch. Somebody
   * who signed up on an Italian phone and then read the site in English
   * went on getting Italian email for ever, including their password reset
   * - which is the one message you get when you are already locked out and
   * least able to cope with a language you do not read.
   *
   * So the switch now saves it. Quietly: nobody pressed this expecting a
   * form to submit, and if the save fails the site language has still
   * changed, which is what they actually asked for.
   *
   * Settings has the same control with the same effect, and says out loud
   * that it decides the language of your email. This is the shortcut.
   */
  const choose = (code) => {
    setLang(code);
    setOpen(false);
    if (!user || user.language === code) return;
    authApi
      .updateProfile({ language: code })
      .then((saved) => setUser(saved.user))
      .catch(() => {
        /* The site has changed language either way - do not shout about it. */
      });
  };

  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[0];

  /**
   * Close when the tap lands anywhere else, and on Escape.
   *
   * Without the first one, a menu opened by accident on a phone has no
   * obvious way out - there is no cursor to move away, and the only escape
   * would be to pick a language you did not want.
   */
  useEffect(() => {
    if (!open) return undefined;

    const onPointer = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div className="lang-switch" ref={boxRef}>
      <button
        type="button"
        className="lang-current"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="listbox"
        // The visible text is two letters, which on its own tells a screen
        // reader nothing about what the button does.
        aria-label={`${t('language.change')} - ${current.name}`}
      >
        {current.label}
      </button>

      {open && (
        <ul className="lang-menu" role="listbox" aria-label={t('language.label')}>
          {LANGUAGES.map((option) => (
            <li key={option.code}>
              <button
                type="button"
                className={`lang-option${option.code === lang ? ' is-current' : ''}`}
                role="option"
                aria-selected={option.code === lang}
                onClick={() => choose(option.code)}
              >
                <span className="lang-option-code">{option.label}</span>
                {/* Named in its own language - somebody hunting for Italian is
                    looking for the word "Italiano". */}
                <span className="lang-option-name">{option.name}</span>
                {option.code === lang && <Check size={14} aria-hidden="true" />}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
